<template>
  <div class="relative">
    <div v-if="loading" class="mt-11 flex justify-center">
      <TheLoader />
    </div>
    <div v-else class="mt-6 flex flex-col" :class="[$attrs.class]">
      <div class="mb-6 hidden pb-3 text-base font-semibold md:block lg:pb-0">
        Benchmark’s flexible pricing tool provides estimated delivered costs of
        several assessed battery raw materials via shipping container to
        selected geographic locations. Our market assessed prices as well as
        third party information and market research is combined to create
        indicative prices.
      </div>

      <div class="rounded-xl bg-white p-5 pb-0 shadow-pm">
        <div v-if="!jwtToken" class="flex justify-end">
          <ButtonContactUs
            text="Contact Us"
            :services="initialFields"
            class="mb-2 !w-full flex-shrink-0 text-sm font-semibold leading-5"
          />
        </div>
        <div class="flex flex-col xl:flex-row">
          <div>
            <h3 class="border-b-2 border-b-p-500 px-3 py-2 font-bold">
              Assessment
            </h3>

            <div
              class="group relative my-2 inline-block w-full xl:my-7 xl:w-[400px]"
            >
              <button
                data-testid="select-assessment"
                class="inline-flex w-full items-center justify-center rounded-xl bg-s-900 p-3 font-semibold text-white group-hover:bg-s-500 xl:justify-between"
              >
                <span class="mr-3.5 line-clamp-1 font-montserrat font-semibold">
                  {{ selectAssessment?.label }}
                </span>
                <ChevronDown
                  class="h-6 min-h-[24px] w-6 min-w-[24px] group-hover:rotate-180"
                />
              </button>
              <div class="absolute z-20 hidden w-full pt-1 group-hover:block">
                <div
                  class="flex flex-col rounded border-b border-s-500 bg-s-900 bg-opacity-[0.97] p-3 shadow-[0_4px_10px_rgba(0,0,0,0.5)]"
                >
                  <div
                    class="flex max-h-[180px] flex-col gap-1 overflow-y-auto"
                  >
                    <div
                      v-for="(assessment, index) in assessmentData"
                      :key="`${index}-${assessment.label}`"
                    >
                      <label
                        :for="`${index}-${assessment.label}`"
                        class="cursor-pointer text-sm font-semibold text-white"
                      >
                        <input
                          :id="`${index}-${assessment.label}`"
                          v-model="selectAssessment"
                          type="radio"
                          :value="assessment"
                          class="mr-2 h-4 w-4 cursor-pointer rounded-full text-p-500 focus:shadow-none focus:ring-0 focus:ring-offset-0"
                        />
                        {{ assessment.label }}
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="flex-grow">
            <h4
              class="px-3 py-2 font-bold xl:border-b-2 xl:border-b-p-500 xl:pl-28"
            >
              Terms
            </h4>
            <div class="flex flex-col xl:my-7 xl:flex-row">
              <span class="p-3 xl:pl-28">
                {{ selectAssessment?.term }}
              </span>
              <div
                data-testid="total-assessment"
                class="p-3 text-right xl:flex-grow"
              >
                <div v-if="selectAssessment?.value">
                  {{ assessmentValue }}
                </div>
                <SummaryTableBlockedData
                  v-else
                  :initial-fields="initialFields"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="flex flex-col xl:flex-row">
          <div>
            <h3 class="px-3 py-2 font-bold xl:border-b-2 xl:border-b-p-500">
              Container Transport
            </h3>
            <div class="flex w-full flex-col">
              <div
                class="group relative my-2 inline-block xl:my-7 xl:w-[400px]"
              >
                <button
                  data-testid="select-container"
                  class="inline-flex w-full items-center justify-center rounded-xl bg-s-900 p-3 font-semibold text-white group-hover:bg-s-500 xl:justify-between"
                >
                  <span
                    class="mr-3.5 line-clamp-1 font-montserrat font-semibold"
                  >
                    {{ selectContainer?.label }}
                  </span>
                  <ChevronDown
                    class="h-6 min-h-[24px] w-6 min-w-[24px] group-hover:rotate-180"
                  />
                </button>
                <div class="absolute z-20 hidden w-full pt-1 group-hover:block">
                  <div
                    class="flex flex-col rounded border-b border-s-500 bg-s-900 bg-opacity-[0.97] p-3 shadow-[0_4px_10px_rgba(0,0,0,0.5)]"
                  >
                    <div
                      class="flex max-h-[180px] flex-col gap-1 overflow-y-auto"
                    >
                      <div
                        v-for="(
                          container, index
                        ) in selectAssessment?.containersTransport"
                        :key="`${index}-${container.label}`"
                      >
                        <label
                          :for="`${index}-${container.label}`"
                          class="cursor-pointer text-sm font-semibold text-white"
                        >
                          <input
                            :id="`${index}-${container.label}`"
                            v-model="selectContainer"
                            type="radio"
                            :value="container"
                            class="mr-2 h-4 w-4 cursor-pointer rounded-full text-p-500 focus:shadow-none focus:ring-0 focus:ring-offset-0"
                          />
                          {{ container.label }}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="flex-grow">
            <h3 class="border-b-2 border-b-p-500 px-3 py-2 font-bold xl:pl-28">
              Shipping Details
            </h3>
            <div class="flex flex-col xl:my-7 xl:flex-row">
              <span class="p-3 xl:pl-28">Inland & Container Rate</span>
              <div class="p-3 text-right xl:flex-grow">
                <div v-if="selectContainer?.value">
                  {{ containerValue }}
                </div>
                <SummaryTableBlockedData
                  v-else
                  :initial-fields="initialFields"
                />
              </div>
            </div>
          </div>
        </div>

        <div>
          <h3 class="border-b-2 border-b-p-500 px-3 py-2 font-bold"> Total </h3>
          <div class="flex justify-between xl:mt-7 xl:justify-normal">
            <span class="px-3 py-2 font-semibold xl:min-w-[400px]">
              Implied Cost
            </span>
            <div class="px-3 py-2 text-right font-semibold xl:ml-28 xl:pl-0">
              $/tonne
            </div>
            <div
              data-testid="total-container"
              class="px-3 py-2 text-right font-semibold xl:flex-grow"
            >
              <div v-if="selectContainer?.total">
                {{ containerTotal }}
              </div>
              <SummaryTableBlockedData v-else :initial-fields="initialFields" />
            </div>
          </div>
        </div>
        <div class="pb-3 text-end text-xs text-[#a3a5ae] lg:pt-1">
          Price does not include taxes and fees
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { PRICE_ASSESSMENTS } from '~/constants/services/constants'
import { logError } from '~/utils/log-error'
import { numberFormatter } from '@/utils/number'
import ButtonContactUs from '~/components/shared/common/ButtonContactUs.vue'
import SummaryTableBlockedData from '~/components/price-assessments/SummaryTableBlockedData.vue'
import ChevronDown from '@/assets/icons/ChevronDown.svg'
import useAuthStore from '~/stores/auth'
import type { TGeneral, TRecord } from '~/src/types/common'

const props = defineProps({
  marketSlug: String,
})

const { $rudderstack } = useNuxtApp()
const authStore = useAuthStore()
const jwtToken = computed(() => authStore.jwtToken)

const loading = ref(true)
const assessmentData = ref<TGeneral[]>([])
const selectAssessment = ref<TRecord | null>(null)
const selectContainer = ref<TRecord | null>(null)
const initialFields = ref([props.marketSlug, PRICE_ASSESSMENTS.slug])

const assessmentValue = computed(
  () => `$${numberFormatter(selectAssessment.value?.value, 2)}`,
)
const containerValue = computed(
  () => `$${numberFormatter(selectContainer.value?.value, 2)}`,
)
const containerTotal = computed(
  () => `$${numberFormatter(selectContainer.value?.total, 2)}`,
)

const getCustomisationTool = async () => {
  try {
    loading.value = true
    assessmentData.value =
      await useNuxtApp().$restClient.getPricesCustomisationToolByProduct(
        props.marketSlug,
      )
  } catch (error) {
    logError(error)
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  await getCustomisationTool()
  selectAssessment.value = assessmentData.value[0]
  selectContainer.value = selectAssessment.value.containersTransport[0]
})

watch(selectAssessment, (newAssessment: TGeneral) => {
  selectContainer.value =
    newAssessment?.containersTransport?.find(
      (container) => container.label === selectContainer.value?.label,
    ) || null

  trackUsage()
})

watch(selectContainer, () => {
  trackUsage()
})

const trackUsage = () => {
  $rudderstack.trackFilterApplied({
    filter_values: {
      selectedContainer: selectContainer.value,
      selectedAssessment: selectAssessment.value,
    },
    filter_type: 'price_data_filter',
    chart_title: 'Price Customisation Tool',
  })
}
</script>
